import React, { useState, useEffect } from 'react'
import { usePlaidLink, PlaidAccount } from 'react-plaid-link'
import toast from 'react-hot-toast'
import { supabase } from '../../services/supabase'
import MoovTosGate from './MoovTosGate'

// Moov can only receive payouts to a checking/savings (depository) account, and
// Plaid only mints a processor token for an account that has auth numbers. If we
// blindly send accounts[0], a user whose first connected account is a credit
// card / loan / investment trips Plaid's NO_AUTH_ACCOUNTS error. Pick the right
// account: prefer checking, then savings, then any depository account.
function pickDepositoryAccount(accounts: PlaidAccount[] = []): PlaidAccount | null {
  const isDepository = (a: PlaidAccount) =>
    (a.type || '').toLowerCase() === 'depository'
  const bySubtype = (sub: string) =>
    accounts.find(a => isDepository(a) && (a.subtype || '').toLowerCase() === sub)
  return (
    bySubtype('checking') ||
    bySubtype('savings') ||
    accounts.find(isDepository) ||
    null
  )
}

interface PlaidBankStepProps {
  onComplete: () => void
  moovAccountId: string | null
  userId: string
  isLoading?: boolean
}

// supabase-js surfaces non-2xx Edge Function responses as a generic
// "Edge Function returned a non-2xx status code" FunctionsHttpError. The real
// reason (Plaid / Moov message) is in error.context (the Response body), so we
// dig it out — otherwise the user is stuck with a meaningless error.
async function resolveFnError(error: any, fallback: string): Promise<string> {
  try {
    const ctx = error?.context
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.clone().json().catch(() => null)
      if (body?.error) return String(body.error)
    }
    if (ctx && typeof ctx.text === 'function') {
      const txt = await ctx.clone().text().catch(() => '')
      if (txt) return txt.slice(0, 300)
    }
  } catch {}
  const msg = error?.message
  if (msg && msg !== 'Edge Function returned a non-2xx status code') return msg
  return fallback
}

const PlaidBankStep: React.FC<PlaidBankStepProps> = ({ onComplete, moovAccountId, userId, isLoading }) => {
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [preparingLink, setPreparingLink] = useState(false)
  const [bankLinked, setBankLinked] = useState(false)
  // Moov requires Terms-of-Service acceptance before the send-funds capability
  // (and therefore bank linking) can work. Gate the Plaid step on it.
  const [tosState, setTosState] = useState<'checking' | 'needed' | 'done'>('checking')

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      // Only checking/savings accounts can receive payouts and support Plaid
      // auth; selecting a credit card / loan here causes NO_AUTH_ACCOUNTS.
      const account = pickDepositoryAccount(metadata.accounts)
      if (!account) {
        toast.error(
          'Please connect a checking or savings account. Other account types (credit cards, loans, investments) can\u2019t receive payouts.',
          { id: 'plaid-success', duration: 6000 }
        )
        return
      }
      toast.loading('Connecting bank account...', { id: 'plaid-success' })
      try {
        const { data, error } = await supabase.functions.invoke('moov-plaid-link-account', {
          body: {
            public_token: public_token,
            account_id: account.id,
            moov_account_id: moovAccountId
          }
        })
        if (error) throw error

        toast.success('Bank account linked successfully!', { id: 'plaid-success' })
        setBankLinked(true)
        setTimeout(() => onComplete(), 1500)
      } catch (error: any) {
        console.error('Error linking bank:', error)
        const msg = await resolveFnError(error, 'Failed to link bank account')
        // Moov rejects a second link to the same bank as a duplicate. If the
        // account is already on file, treat it as success so the user isn't
        // dead-ended on a retry.
        if (/already|duplicate|exists/i.test(msg)) {
          toast.success('Bank account linked successfully!', { id: 'plaid-success' })
          setBankLinked(true)
          setTimeout(() => onComplete(), 1500)
          return
        }
        // Plaid's raw NO_AUTH_ACCOUNTS wording is confusing; translate it.
        const friendly = /no_auth_accounts|no valid checking or savings/i.test(msg)
          ? 'That account can\u2019t receive payouts. Please connect a checking or savings account (not a credit card, loan, or investment account).'
          : msg
        toast.error(friendly, { id: 'plaid-success', duration: 6000 })
      }
    },
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link error:', err)
        toast.error('Failed to connect bank account')
      }
    }
  })

  const preparePlaidLink = async () => {
    if (!moovAccountId) {
      toast.error('Please complete Moov account verification first')
      return
    }
    setPreparingLink(true)
    toast.loading('Preparing Plaid Link...', { id: 'plaid-link' })
    try {
      const { data, error } = await supabase.functions.invoke('plaid-create-link-token', {
        body: { userId }
      })
      if (error) throw error
      if (data?.link_token) {
        setLinkToken(data.link_token)
        toast.success('Ready to connect bank', { id: 'plaid-link' })
      } else {
        throw new Error('No link token received')
      }
    } catch (error: any) {
      console.error('Error preparing Plaid:', error)
      const msg = await resolveFnError(error, 'Failed to prepare bank connection')
      toast.error(msg, { id: 'plaid-link' })
    } finally {
      setPreparingLink(false)
    }
  }

  // Check whether Moov ToS has been accepted (send-funds enabled). If not, we
  // must collect acceptance before Plaid, otherwise bank linking 500s.
  useEffect(() => {
    if (!moovAccountId) return
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await supabase.functions.invoke('moov-get-account', {
          body: { accountId: moovAccountId }
        })
        const caps: any[] = data?.capabilities || []
        const sendFunds = caps.find(c => c.capability === 'send-funds')
        if (!cancelled) setTosState(sendFunds?.status === 'enabled' ? 'done' : 'needed')
      } catch {
        // Safe default: collect ToS rather than letting the bank link fail.
        if (!cancelled) setTosState('needed')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [moovAccountId])

  // Auto-prepare Plaid link once ToS is satisfied
  useEffect(() => {
    if (tosState === 'done' && moovAccountId && !linkToken) preparePlaidLink()
  }, [tosState, moovAccountId])

  useEffect(() => {
    if (ready && linkToken && !bankLinked) open()
  }, [ready, linkToken, open, bankLinked])

  if (!moovAccountId) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Moov Account Required</h3>
        <p className="text-gray-600">Please complete the Moov account verification step first before linking your bank.</p>
      </div>
    )
  }

  // Still determining whether Moov ToS acceptance is required
  if (tosState === 'checking') {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="text-gray-600">Checking payout setup…</p>
      </div>
    )
  }

  // Collect Moov Terms-of-Service acceptance before bank linking
  if (tosState === 'needed' && moovAccountId) {
    return <MoovTosGate moovAccountId={moovAccountId} onAccepted={() => setTosState('done')} />
  }

  if (bankLinked) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Bank Account Linked!</h3>
        <p className="text-gray-600">Your bank account has been successfully connected. You're all set to receive payouts!</p>
      </div>
    )
  }

  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">Connect Your Bank Account</h3>
      <p className="text-gray-600 mb-8 max-w-md mx-auto">
        We use Plaid to securely connect your bank account. Your credentials are never stored on our servers.
      </p>
      <div className="bg-gray-900/50 backdrop-blur-sm border border-white/20 rounded-lg p-6 max-w-md mx-auto mb-8">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="text-left">
            <h4 className="text-sm font-semibold text-white mb-1">Secure & Private</h4>
            <p className="text-xs text-gray-300">Plaid uses bank-level encryption. We never see your login credentials.</p>
          </div>
        </div>
      </div>

      {preparingLink ? (
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="text-gray-600">Preparing secure connection...</p>
        </div>
      ) : linkToken ? (
        <button
          onClick={() => open()}
          disabled={!ready || isLoading}
          className="px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
        >
          {ready ? 'Connect Bank Account' : 'Loading...'}
        </button>
      ) : (
        <button
          onClick={preparePlaidLink}
          disabled={isLoading}
          className="px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
        >
          Prepare Connection
        </button>
      )}
      <p className="text-xs text-gray-500 mt-6">By connecting your bank account, you agree to Plaid's Terms of Service</p>
    </div>
  )
}

export default PlaidBankStep
